/* eslint-disable react-hooks/refs */
import CloseButton from "components/buttons/CloseButton"
import {
  CSSProperties,
  type JSX,
  ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { useTheme } from "styled-components"
import type { Theme } from "styles/theme"

import type { ToneColorOverrides } from "../ToastStack/types"
import {
  ToastActions,
  ToastBody,
  ToastHeader,
  ToastTitle,
  type ToastTone,
  ToastWrapper
} from "./styled"

export type ToastNotificationProps = {
  tone?: ToastTone
  title?: ReactNode
  message: ReactNode
  actions?: ReactNode
  onDismiss?: () => void
  dismissLabel?: string
  autoDismiss?: boolean
  autoDismissDelay?: number
  reducedMotion?: boolean
  ariaLive?: "polite" | "assertive"
  visible?: boolean
  className?: string
  style?: CSSProperties
  solidBackground?: boolean
  toneColors?: ToneColorOverrides
  onDelivered?: () => void
  ref?: Ref<HTMLDivElement>
}

const DEFAULT_DELAY = 5000

const useToastAccessibility = (
  tone: ToastTone,
  ariaLive: ToastNotificationProps["ariaLive"]
): {
  role: "alert" | "status"
  computedAriaLive: "polite" | "assertive"
} => {
  const role = useMemo(() => (tone === "alert" ? "alert" : "status"), [tone])
  const computedAriaLive = useMemo(
    () => ariaLive ?? (tone === "alert" ? "assertive" : "polite"),
    [ariaLive, tone]
  )

  return { role, computedAriaLive }
}

type ToastMarkupParams = {
  title?: ReactNode
  message: ReactNode
  actions?: ReactNode
  onDismiss?: () => void
  dismissLabel: string
  dismissColor?: string
  solidBackground: boolean
  tone: ToastTone
  toneColors?: ToneColorOverrides
  reducedMotion?: boolean
  visible: boolean
  className?: string
  ariaLive: "polite" | "assertive"
  role: "alert" | "status"
  forwardedRef?: Ref<HTMLDivElement>
  style?: CSSProperties
  onClose: () => void
}

const buildToastMarkup = ({
  title,
  message,
  actions,
  onDismiss,
  dismissLabel,
  dismissColor,
  solidBackground,
  tone,
  toneColors,
  reducedMotion,
  visible,
  className,
  ariaLive,
  role,
  forwardedRef,
  style,
  onClose
}: ToastMarkupParams): JSX.Element => (
  <ToastWrapper
    $reducedMotion={reducedMotion}
    $solidBackground={solidBackground}
    $tone={tone}
    $toneColors={toneColors}
    $visible={visible}
    aria-live={ariaLive}
    className={className}
    data-solid-background={solidBackground ? "true" : "false"}
    ref={forwardedRef}
    role={role}
    style={style}
    tabIndex={0}
  >
    <ToastHeader>
      {title ? <ToastTitle>{title}</ToastTitle> : null}

      {onDismiss ? (
        <CloseButton
          customColor={dismissColor}
          label={dismissLabel}
          onClick={onClose}
          size={28}
          tone={tone}
          variant={solidBackground ? "transparent" : "surface"}
        />
      ) : null}
    </ToastHeader>

    <ToastBody
      $solidBackground={solidBackground}
      $tone={tone}
      $toneColors={toneColors}
    >
      {message}
    </ToastBody>

    {actions ? <ToastActions>{actions}</ToastActions> : null}
  </ToastWrapper>
)

const useToastAutoDismiss = ({
  isControlled,
  autoDismiss,
  autoDismissDelay,
  onDismiss,
  setUncontrolledVisible
}: {
  isControlled: boolean
  autoDismiss: boolean
  autoDismissDelay: number
  onDismiss?: () => void
  setUncontrolledVisible: (visible: boolean) => void
}) => {
  useEffect(() => {
    if (isControlled || !autoDismiss || !onDismiss) {
      return undefined
    }

    const timeout = setTimeout(() => {
      setUncontrolledVisible(false)
      onDismiss()
    }, autoDismissDelay)

    return () => {
      clearTimeout(timeout)
    }
  }, [
    autoDismiss,
    autoDismissDelay,
    isControlled,
    onDismiss,
    setUncontrolledVisible
  ])
}

const useTelemetryDelivery = (
  visible: boolean,
  contextKey: readonly unknown[],
  onDelivered?: () => void
) => {
  const previousVisibleRef = useRef(false)
  const previousContextRef = useRef<readonly unknown[] | null>(null)

  useEffect(() => {
    const wasVisible = previousVisibleRef.current
    const previousContext = previousContextRef.current

    previousVisibleRef.current = visible
    previousContextRef.current = contextKey

    if (!visible) {
      return
    }

    const becameVisible = !wasVisible
    const contentChanged =
      previousContext !== null && previousContext !== contextKey

    if (becameVisible || contentChanged) {
      onDelivered?.()
    }
  }, [contextKey, onDelivered, visible])
}

// eslint-disable-next-line max-statements
const ToastNotification = ({
  tone = "info",
  title,
  message,
  actions,
  onDismiss,
  dismissLabel = "Dismiss",
  autoDismiss = true,
  autoDismissDelay = DEFAULT_DELAY,
  reducedMotion,
  ariaLive,
  visible: visibleProp,
  className,
  style,
  solidBackground = false,
  toneColors,
  onDelivered,
  ref: forwardedRef
}: ToastNotificationProps) => {
  const theme = useTheme() as Theme
  const [uncontrolledVisible, setUncontrolledVisible] = useState(true)
  const isControlled = visibleProp !== undefined
  const visible = isControlled ? visibleProp : uncontrolledVisible

  useToastAutoDismiss({
    isControlled,
    autoDismiss,
    autoDismissDelay,
    onDismiss,
    setUncontrolledVisible
  })

  const handleDismiss = useCallback(() => {
    if (!isControlled) {
      setUncontrolledVisible(false)
    }
    onDismiss?.()
  }, [isControlled, onDismiss])

  const { role, computedAriaLive } = useToastAccessibility(tone, ariaLive)

  const toneTextColor = toneColors?.[tone]?.text ?? theme.palette[tone].contrast
  const dismissColor = solidBackground ? toneTextColor : undefined

  const deliveryContext = useMemo(
    () => [title, message] as const,
    [message, title]
  )

  useTelemetryDelivery(visible, deliveryContext, onDelivered)

  const handleCloseButtonClick = useCallback(() => {
    handleDismiss()
  }, [handleDismiss])

  return buildToastMarkup({
    actions,
    ariaLive: computedAriaLive,
    className,
    dismissColor,
    dismissLabel,
    forwardedRef,
    message,
    onClose: handleCloseButtonClick,
    onDismiss,
    reducedMotion,
    role,
    solidBackground,
    style,
    title,
    tone,
    toneColors,
    visible
  })
}

export default ToastNotification
